// frontend/src/pages/crm/TaskDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { taskAPI } from '../../utils/crmApi';
import {
    ArrowLeftIcon,
    PencilIcon,
    TrashIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    UserIcon,
    CalendarIcon,
    EyeIcon,
    EyeSlashIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const TaskDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [updatingStep, setUpdatingStep] = useState(null);

    // Determine where to navigate back to
    const getBackNavigation = () => {
        // Check if we came from a client detail page
        if (location.state?.fromClientDetail) {
            return `/clients/${location.state.clientId}`;
        }
        
        // Check if we came from a specific client's tasks
        if (location.state?.clientId) {
            return `/clients/${location.state.clientId}`;
        }
        
        // Check if the referrer was a client detail page (check both /crm/clients/ and /clients/)
        if (document.referrer && (document.referrer.includes('/crm/clients/') || document.referrer.includes('/clients/'))) {
            const clientId = document.referrer.match(/\/(?:crm\/)?clients\/([^\/]+)/)?.[1];
            if (clientId) {
                return `/clients/${clientId}`;
            }
        }
        
        // Default to tasks list
        return '/tasks';
    };

    const handleBackNavigation = () => {
        navigate(getBackNavigation());
    };

    useEffect(() => {
        if (id) {
            loadTask();
        }
    }, [id]);

    const loadTask = async () => {
        try {
            setLoading(true);
            const response = await taskAPI.getById(id);
            if (response.data.success) {
                setTask(response.data.data);
            } else {
                setError('Task not found');
            }
        } catch (error) {
            console.error('Failed to load task:', error);
            setError('Failed to load task');
            toast.error('Failed to load task');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this task?')) {
            try {
                const response = await taskAPI.delete(id);
                if (response.data.success) {
                    toast.success('Task deleted successfully');
                    handleBackNavigation();
                }
            } catch (error) {
                console.error('Failed to delete task:', error);
                toast.error('Failed to delete task');
            }
        }
    };

    const handleStatusUpdate = async (newStatus) => {
        if (updatingStatus) return;
        
        try {
            setUpdatingStatus(true);
            const response = await taskAPI.update(id, { status: newStatus });
            if (response.data.success) {
                setTask(prev => ({ ...prev, status: newStatus }));
                toast.success('Task status updated successfully');
            }
        } catch (error) {
            console.error('Failed to update task status:', error);
            toast.error('Failed to update task status');
        } finally {
            setUpdatingStatus(false);
        }
    };

    const handleStepUpdate = async (stepIndex, newStatus) => {
        if (updatingStep === stepIndex) return;
        
        try {
            setUpdatingStep(stepIndex);
            const response = await taskAPI.updateStep(id, stepIndex, { status: newStatus });
            if (response.data.success) {
                setTask(prev => ({
                    ...prev,
                    steps: prev.steps.map((step, index) => 
                        index === stepIndex ? { ...step, status: newStatus } : step
                    )
                }));
                toast.success('Step status updated successfully');
            }
        } catch (error) {
            console.error('Failed to update step status:', error);
            toast.error('Failed to update step status');
        } finally {
            setUpdatingStep(null);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return 'bg-green-100 text-green-800';
            case 'in_progress':
                return 'bg-blue-100 text-blue-800';
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'overdue':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-muted text-foreground';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high':
                return 'bg-red-100 text-red-800';
            case 'medium':
                return 'bg-yellow-100 text-yellow-800';
            case 'low':
                return 'bg-green-100 text-green-800';
            default:
                return 'bg-muted text-foreground';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error || !task) {
        return (
            <div className="p-6">
                <div className="text-center">
                    <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-muted-foreground" />
                    <h3 className="mt-2 text-sm font-medium text-foreground">Task not found</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{error || 'The task you are looking for does not exist.'}</p>
                    <div className="mt-6">
                        <button
                            onClick={handleBackNavigation}
                            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <ArrowLeftIcon className="h-4 w-4 mr-2" />
                            Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={handleBackNavigation}
                            className="inline-flex items-center px-3 py-2 border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground shadow-sm text-sm leading-4 font-medium rounded-md text-foreground bg-card hover:bg-muted"
                        >
                            <ArrowLeftIcon className="h-4 w-4 mr-2" />
                            Back
                        </button>
                        <h1 className="text-2xl font-bold text-foreground">{task.title}</h1>
                    </div>
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={() => navigate(`/tasks/${id}/edit`)}
                            className="inline-flex items-center px-3 py-2 border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground shadow-sm text-sm leading-4 font-medium rounded-md text-foreground bg-card hover:bg-muted"
                        >
                            <PencilIcon className="h-4 w-4 mr-2" />
                            Edit
                        </button>
                        <button
                            onClick={handleDelete}
                            className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                        >
                            <TrashIcon className="h-4 w-4 mr-2" />
                            Delete
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Status and Priority */}
                    <div className="bg-card shadow rounded-lg p-6">
                        <h2 className="text-lg font-medium text-foreground mb-4">Task Details</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                                <div className="relative">
                                    <select
                                        value={task.status}
                                        onChange={(e) => handleStatusUpdate(e.target.value)}
                                        disabled={updatingStatus}
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border-0 cursor-pointer ${getStatusColor(task.status)} ${updatingStatus ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'}`}
                                    >
                                        <option value="not_started">Not Started</option>
                                        <option value="in_progress">In Progress</option>
                                        <option value="completed">Completed</option>
                                        <option value="on_hold">On Hold</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                    {updatingStatus && (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-3 w-3 border-b border-border"></div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">Priority</label>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                                    {task.priority?.toUpperCase()}
                                </span>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">Visibility</label>
                                {task.visibleToClient ? (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" title="This task is visible to the client in their portal">
                                        <EyeIcon className="h-3.5 w-3.5" />
                                        Visible to Client
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400" title="This task is not visible to the client">
                                        <EyeSlashIcon className="h-3.5 w-3.5" />
                                        Private
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    {task.description && (
                        <div className="bg-card shadow rounded-lg p-6">
                            <h3 className="text-lg font-medium text-foreground mb-3">Description</h3>
                            <p className="text-foreground whitespace-pre-wrap">{task.description}</p>
                        </div>
                    )}

                    {/* Steps */}
                    {task.steps && task.steps.length > 0 && (
                        <div className="bg-card shadow rounded-lg p-6">
                            <h3 className="text-lg font-medium text-foreground mb-4">Task Steps</h3>
                            <div className="space-y-3">
                                {task.steps.map((step, index) => (
                                    <div key={index} className="flex items-start space-x-3">
                                        <div className="flex-shrink-0">
                                            <button
                                                onClick={() => handleStepUpdate(index, step.status === 'done' ? 'todo' : 'done')}
                                                disabled={updatingStep === index}
                                                className={`flex items-center justify-center w-5 h-5 rounded-full border-2 transition-colors ${
                                                    step.status === 'done' 
                                                        ? 'bg-green-500 border-green-500 text-white' 
                                                        : 'border-input hover:border-green-500'
                                                } ${updatingStep === index ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                            >
                                                {step.status === 'done' && <CheckCircleIcon className="h-3 w-3" />}
                                                {updatingStep === index && (
                                                    <div className="animate-spin rounded-full h-3 w-3 border-b border-border"></div>
                                                )}
                                            </button>
                                        </div>
                                        <div className="flex-1">
                                            <p className={`text-sm ${step.status === 'done' ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                                                {step.title || step.description}
                                            </p>
                                            {step.description && step.title && (
                                                <p className="text-xs text-muted-foreground mt-1">{step.description}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Client Info */}
                    {task.client && (
                        <div className="bg-card shadow rounded-lg p-6">
                            <h3 className="text-lg font-medium text-foreground mb-4">Client</h3>
                            <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0">
                                    <UserIcon className="h-8 w-8 text-muted-foreground" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-foreground">
                                        {task.client.firstName || task.client.lastName ? `${task.client.firstName || ''} ${task.client.lastName || ''}`.trim() : (task.client.name || 'Unknown Client')}
                                    </p>
                                    <p className="text-sm text-muted-foreground">{task.client.email}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Dates */}
                    <div className="bg-card shadow rounded-lg p-6">
                        <h3 className="text-lg font-medium text-foreground mb-4">Timeline</h3>
                        <div className="space-y-3">
                            <div className="flex items-center space-x-3">
                                <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                                <div>
                                    <p className="text-sm font-medium text-foreground">Created</p>
                                    <p className="text-sm text-muted-foreground">
                                        {new Date(task.createdAt).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>
                            {task.dueDate && (
                                <div className="flex items-center space-x-3">
                                    <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                                    <div>
                                        <p className="text-sm font-medium text-foreground">Due Date</p>
                                        <p className="text-sm text-muted-foreground">
                                            {new Date(task.dueDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskDetail;




